import React from "react";
import {
  TLBaseShape,
  RecordProps,
  T,
  Rectangle2d,
  TLDefaultHorizontalAlignStyle,
  TLDefaultVerticalAlignStyle,
  Geometry2d,
  WeakCache,
  TLResizeInfo,
  Vec,
  toDomPrecision,
  Box,
  SvgExportContext,
  ShapeUtil,
  Editor,
  resizeBox,
  useValue,
} from "@tldraw/tldraw";
import { isEqual } from "lodash";
import { getFontDefForExport, getTextSize, resizeScaled, useTextShapeKeydownHandler } from "./text-helper";
import { SvgTextLabel } from "./SvgExportContext";
import { TextLabel } from "./TextLabel";
import { mixColors } from "../CustomCloudUtil";
import { ShapeUtilHelper } from "../ShapeUtilHelper";
import { HOVER_EFFECT } from "../constants";
import { defaultScale } from "../../constants";

export type CustomTextUtilType = TLBaseShape<
  "customText",
  {
    color: string;
    size: number;
    font: string;
    textAlign: TLDefaultHorizontalAlignStyle;
    w: number;
    h: number;
    text: string;
    autoSize: boolean;
    fixedHeight: boolean;
    fill: string;
    outlineColor: string;
    transparency: number;
    forAbilityEditable?: boolean;
    fontWeight?: number | undefined;
    underline?: string;
    lineThrough?: string;
    italic?: string;
    textJustify?: string;
    strokeWidth?: number;
    lineHeight?: number;
    letterSpacing?: number;
    borderWidth?: number;
    borderColor?: string;
    borderSolid?: "solid" | "dashed";
    borderTopStyle?: string;
    borderBottomStyle?: string;
    borderLeftStyle?: string;
    borderRightStyle?: string;
    isShowBorderTop?: boolean;
    isShowBorderBottom?: boolean;
    isShowBorderLeft?: boolean;
    isShowBorderRight?: boolean;
    rounded?: number;
    filterBlur?: number;
    flipH?: boolean;
    flipV?: boolean;
    isCreatedFilterBlur?: boolean;
    filterBlurValueWhenHidden?: number;
    outShadowArray?: {
      [key: string]: {
        id: string;
        dx: number;
        dy: number;
        shadowBlur: number;
        color: string;
        opacity: number;
        isHidden: boolean;
      };
    };
    inShadowArray?: {
      [key: string]: {
        id: string;
        dx: number;
        dy: number;
        shadowBlur: number;
        color: string;
        opacity: number;
        isHidden: boolean;
      };
    };
    previousBorderColor?: string;
    isCreatedBorderItem?: boolean;
    colorMix: {
      [key: string]: {
        color: string;
        isHidden: boolean;
      };
    };
    outlineColorMix: {
      [key: string]: {
        color: string;
        isHidden: boolean;
      };
    };
    isHiddenLayerBlur?: boolean;
    isHiddenBorder?: boolean;
    isShowIndicator?: boolean;
    isScale?: boolean;
    scale?: number;
    [key: string]: any;
  }
>;

const sizeCache = new WeakCache<CustomTextUtilType["props"], { height: number; width: number }>();

export class CustomTextUtil extends ShapeUtil<CustomTextUtilType> {
  private shapeUtilHelper: ShapeUtilHelper;

  constructor(editor: Editor) {
    super(editor);
    this.shapeUtilHelper = new ShapeUtilHelper(editor);
  }

  static type = "customText" as const;
  static props: any;

  private isResizing = false;
  private _preResizeState: Map<string, { autoSize: boolean; fixedHeight: boolean }> = new Map();

  getDefaultProps(): CustomTextUtilType["props"] {
    return {
      color: "red",
      size: 18,
      w: 170,
      h: 1,
      text: "",
      font: "Inter",
      textAlign: "start" as TLDefaultHorizontalAlignStyle,
      autoSize: true,
      fixedHeight: false,
      fill: "#000000",
      outlineColor: "#000000",
      transparency: 1,
      forAbilityEditable: false,
      fontWeight: 400,
      underline: "none",
      lineThrough: "none",
      italic: "normal",
      textJustify: "none",
      strokeWidth: 0,
      lineHeight: 1,
      letterSpacing: 1,
      borderWidth: 0,
      borderColor: "#000000",
      borderSolid: "solid",
      borderTopStyle: "solid",
      borderBottomStyle: "solid",
      borderLeftStyle: "solid",
      borderRightStyle: "solid",
      isShowBorderTop: true,
      isShowBorderBottom: true,
      isShowBorderLeft: true,
      isShowBorderRight: true,
      rounded: 0,
      filterBlur: 0,
      flipH: false,
      flipV: false,
      isCreatedFilterBlur: false,
      filterBlurValueWhenHidden: 0,
      outShadowArray: {},
      inShadowArray: {},
      previousBorderColor: "#000000",
      isCreatedBorderItem: false,
      colorMix: {
        "0": { color: "#000000", isHidden: false },
      },
      outlineColorMix: {},
      isHiddenLayerBlur: false,
      isHiddenBorder: false,
      isShowIndicator: true,
      isScale: false,
      scale: defaultScale,
    };
  }

  getMinDimensions(shape: CustomTextUtilType) {
    // For autoSize shapes, don't use cache during initial load to avoid Roboto fallback issues
    // Cache only works correctly after fonts are fully loaded
    if (shape.props.autoSize) {
      // Always recalculate for autoSize to ensure correct dimensions with loaded fonts
      return getTextSize(this.editor, shape.props);
    }
    // For non-autoSize shapes, use the manually set width (w) directly
    const textSize = getTextSize(this.editor, shape.props);
    return {
      width: shape.props.w, // Use the manually set width
      height: textSize.height, // But still calculate height based on text content
    };
  }

  getGeometry(shape: CustomTextUtilType): Geometry2d {
    const { scale, autoSize, fixedHeight } = shape.props;

    let width: number;
    let height: number;

    // Always calculate dimensions from current text when autoSize is true
    // This ensures the blue selection box updates immediately when autoSize is toggled
    const dimensions = this.getMinDimensions(shape)!;

    if (autoSize) {
      // For autoSize, always use calculated width and height from text
      width = dimensions.width;
      height = dimensions.height;
    } else if (fixedHeight) {
      // For fixed size mode (both width and height are manual)
      width = shape.props.w;
      height = shape.props.h;
    } else {
      // For fixed width mode (width manual, height auto)
      width = shape.props.w;
      height = dimensions.height; // Calculate height based on text content
    }

    const adjustedHeight = height * (scale ?? 1);
    const adjustedWidth = width * (scale ?? 1);

    return new Rectangle2d({
      width: adjustedWidth,
      height: adjustedHeight,
      isFilled: true,
      isLabel: true,
    });
  }

  getText(shape: CustomTextUtilType) {
    return shape.props.text;
  }

  canEdit() {
    return true;
  }

  override onBeforeCreate(next: CustomTextUtilType) {
    // Fix text dimensions when shape is created/hydrated from storage
    // This is crucial for fixing the Roboto fallback width issue on page reload
    if (!next.props.autoSize) return next;

    const fontFamily = next.props.font || "Inter";
    const globalDoc = globalThis.document as Document;

    // Check if font is loaded, if not, schedule a fix after load
    if (!globalDoc.fonts.check(`${next.props.size}px "${fontFamily}"`)) {
      // Load font asynchronously and update shape later
      // Increase timeout for backend-loaded fonts
      globalDoc.fonts.load(`${next.props.size}px "${fontFamily}"`).then(() => {
        // Increased delay to account for backend font loading
        setTimeout(() => {
          const currentShape = this.editor.getShape(next.id);
          if (currentShape && currentShape.type === "customText") {
            const calculatedSize = getTextSize(this.editor, currentShape.props as CustomTextUtilType["props"]);
            const currentWidth = (currentShape.props as CustomTextUtilType["props"]).w;

            if (Math.abs(calculatedSize.width - currentWidth) > 1) {
              this.editor.updateShapes([
                {
                  id: currentShape.id,
                  type: currentShape.type,
                  props: { w: calculatedSize.width },
                },
              ]);
            }
          }
        }, 400); // Increased from 200ms to 400ms
      });

      return next;
    }

    // Font is already loaded, calculate correct dimensions immediately
    const calculatedSize = getTextSize(this.editor, next.props);
    const currentWidth = next.props.w;

    if (Math.abs(calculatedSize.width - currentWidth) > 1) {
      return {
        ...next,
        props: {
          ...next.props,
          w: calculatedSize.width,
        },
      };
    }

    return next;
  }

  hideSelectionBoundsFg(_shape: CustomTextUtilType): boolean {
    return false; // outline visibility is controlled by isChangingStyle in useHideSelectionOnChange
  }
  hideSelectionBoundsBg(shape: CustomTextUtilType): boolean {
    return false; // Always show selection bounds regardless of opacity
  }
  hideResizeHandles(shape: CustomTextUtilType): boolean {
    return false; // Always show resize handles regardless of opacity
  }
  override isAspectRatioLocked(shape: CustomTextUtilType) {
    return !!shape.props.isScale; // Lock aspect ratio when scale tool is active
  }

  component(shape: CustomTextUtilType) {
    const handleKeyDown = useTextShapeKeydownHandler(shape.id, this.editor);
    const props = this.shapeUtilHelper.getSafeProps(shape);
    const {
      font,
      size,
      text,
      color,
      scale,
      textAlign,
      verticalAlign,
      fill,
      outlineColor,
      transparency,
      forAbilityEditable,
      fontWeight,
      underline,
      lineThrough,
      italic,
      textJustify,
      strokeWidth,
      lineHeight,
      letterSpacing,
      borderWidth,
      borderColor,
      borderSolid,
      borderTopStyle,
      borderBottomStyle,
      borderLeftStyle,
      borderRightStyle,
      isShowBorderTop,
      isShowBorderBottom,
      isShowBorderLeft,
      isShowBorderRight,
      rounded,
      filterBlur,
      flipH,
      flipV,
      isCreatedFilterBlur,
      filterBlurValueWhenHidden,
      outShadowArray,
      colorMix,
      outlineColorMix,
      inShadowArray,
      isHiddenLayerBlur,
      isHiddenBorder,
      fixedHeight,
    } = props;

    // Fix text dimensions on mount and when font properties change
    // This prevents text wrapping issues after page reload when fonts load
    React.useEffect(() => {
      if (!shape.props.autoSize) return;

      let isCancelled = false;

      const updateDimensions = async () => {
        const fontFamily = shape.props.font || "Roboto";

        try {
          // Wait for the font to be loaded with the actual size
          await document.fonts.load(`${shape.props.size}px "${fontFamily}"`);

          if (isCancelled) return;

          // Multiple attempts to ensure font is fully rendered
          const attempts = [100, 250, 500, 800];

          for (let i = 0; i < attempts.length; i++) {
            if (isCancelled) return;

            const delay = attempts[i];
            await new Promise((resolve) => setTimeout(resolve, delay));

            if (isCancelled) return;

            // Force a reflow to ensure font is applied
            document.body.offsetHeight;

            const calculatedSize = getTextSize(this.editor, shape.props);
            const currentWidth = shape.props.w;

            // On first attempt, just measure and continue
            if (i === 0) continue;

            // If there's a significant difference between calculated and stored width
            if (Math.abs(calculatedSize.width - currentWidth) > 1) {
              // Update width
              this.editor.updateShapes([
                {
                  id: shape.id,
                  type: shape.type,
                  props: { w: calculatedSize.width },
                },
              ]);

              // Force a complete re-render by toggling a dummy prop
              setTimeout(() => {
                const currentShape = this.editor.getShape(shape.id);
                if (currentShape && currentShape.type === "customText") {
                  // Toggle isShowIndicator to force React re-render
                  const currentIndicator = (currentShape.props as any).isShowIndicator;
                  this.editor.updateShapes([
                    {
                      id: shape.id,
                      type: shape.type,
                      props: { isShowIndicator: !currentIndicator },
                    },
                  ]);

                  setTimeout(() => {
                    this.editor.updateShapes([
                      {
                        id: shape.id,
                        type: shape.type,
                        props: { isShowIndicator: currentIndicator },
                      },
                    ]);
                  }, 10);
                }
              }, 10);

              // If we successfully updated, break out of the loop
              break;
            }
          }
        } catch (error) {
          if (isCancelled) return;

          // Fallback: try with delays anyway
          await new Promise((resolve) => setTimeout(resolve, 200));

          if (isCancelled) return;

          const calculatedSize = getTextSize(this.editor, shape.props);
          const currentWidth = shape.props.w;

          if (Math.abs(calculatedSize.width - currentWidth) > 1) {
            this.editor.updateShapes([
              {
                id: shape.id,
                type: shape.type,
                props: { w: calculatedSize.width },
              },
            ]);

            // Force React to re-render by toggling a dummy prop
            setTimeout(() => {
              const currentShape = this.editor.getShape(shape.id);
              if (currentShape && currentShape.type === "customText") {
                const currentIndicator = (currentShape.props as any).isShowIndicator;
                this.editor.updateShapes([
                  {
                    id: shape.id,
                    type: shape.type,
                    props: { isShowIndicator: !currentIndicator },
                  },
                ]);

                setTimeout(() => {
                  this.editor.updateShapes([
                    {
                      id: shape.id,
                      type: shape.type,
                      props: { isShowIndicator: currentIndicator },
                    },
                  ]);
                }, 10);
              }
            }, 10);
          }
        }
      };

      updateDimensions();

      return () => {
        isCancelled = true;
      };
    }, [
      shape.props.size,
      shape.props.font,
      shape.props.fontWeight,
      shape.props.letterSpacing,
      shape.props.lineHeight,
      shape.props.text,
    ]);

    const visibleColors = Object.values(colorMix as any).filter((c: any) => !c.isHidden);
    const calculatedFill = visibleColors.length > 0 ? mixColors(visibleColors.map((c: any) => c.color)) : "transparent";

    const visibleOutlineColors = Object.values(outlineColorMix as any).filter((c: any) => !c.isHidden);
    const calculatedOutlineColor =
      visibleOutlineColors.length > 0 ? mixColors(visibleOutlineColors.map((c: any) => c.color)) : "transparent";

    const editor = this.editor;

    // Subscribe each text shape only to the *booleans* derived from the
    // editor's editing/selection signals (not to the raw signal record).
    // See CustomRectUtil for the hover-equivalent. Without `useValue` here,
    // a "select all" with thousands of text shapes blocks for seconds
    // because each of the 2000 shapes re-runs its full `component()` on
    // every selection / edit signal tick — even though the boolean output
    // is the same for 1999 of them.
    const isEditingAnything = useValue("isEditingAnything", () => editor.getEditingShapeId() !== null, [editor]);
    const isEditingThisShape = useValue("isEditingThisShape", () => editor.getEditingShapeId() === shape.id, [
      editor,
      shape.id,
    ]);
    const showHtmlContainer = isEditingAnything || shape.props.text;

    const { width, height } = this.getMinDimensions(shape);
    const isSelected = useValue("isOnlySelected", () => editor.getOnlySelectedShapeId() === shape.id, [
      editor,
      shape.id,
    ]);

    const handleClick = () => {
      this.editor.updateShapes([
        {
          ...shape,
          props: {
            ...shape.props,
            forAbilityEditable: !forAbilityEditable,
          },
        },
      ]);
    };

    // Subscribe only to the *boolean* "is this shape hovered" — see
    // CustomRectUtil. Avoids O(N) re-renders on every hover transition.
    const isHovered = useValue("isHovered", () => editor.getHoveredShapeId() === shape.id, [editor, shape.id]);

    return (
      <>
        <TextLabel
          shapeId={shape.id}
          classNamePrefix="tl-text-shape-label-custom"
          type="text"
          font={font}
          underline={underline}
          lineThrough={lineThrough}
          italic={italic}
          textJustify={textJustify}
          strokeWidth={strokeWidth ?? 0}
          outlineColor={outlineColor}
          fontWeight={fontWeight}
          fontSize={size}
          align={textAlign as TLDefaultHorizontalAlignStyle}
          verticalAlign={"middle" as TLDefaultVerticalAlignStyle}
          text={text}
          labelColor={fill}
          isSelected={isSelected}
          textWidth={width}
          textHeight={height}
          style={{
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
          wrap
          editor={editor}
          transparency={transparency}
          onKeyDown={handleKeyDown}
          isHovered={isHovered}
          lineHeight={lineHeight ?? 1}
          letterSpacing={letterSpacing ?? 0}
          isEditingAnything={isEditingAnything}
          onClick={handleClick}
          borderWidth={borderWidth ?? 0}
          borderColor={borderColor ?? "#000000"}
          borderSolid={borderSolid ?? "solid"}
          borderTopStyle={borderTopStyle ?? "solid"}
          borderBottomStyle={borderBottomStyle ?? "solid"}
          borderLeftStyle={borderLeftStyle ?? "solid"}
          borderRightStyle={borderRightStyle ?? "solid"}
          isShowBorderTop={isShowBorderTop ?? true}
          isShowBorderBottom={isShowBorderBottom ?? true}
          isShowBorderLeft={isShowBorderLeft ?? true}
          isShowBorderRight={isShowBorderRight ?? true}
          rounded={rounded ?? 0}
          filterBlur={filterBlur ?? 0}
          isResizing={this.isResizing}
          flipH={flipH ?? false}
          flipV={flipV ?? false}
          isCreatedFilterBlur={isCreatedFilterBlur ?? false}
          filterBlurValueWhenHidden={filterBlurValueWhenHidden ?? 0}
          outShadowArray={outShadowArray}
          inShadowArray={inShadowArray}
          colorMix={colorMix}
          calculatedFill={calculatedFill}
          isHiddenLayerBlur={isHiddenLayerBlur ?? false}
          isHiddenBorder={isHiddenBorder ?? false}
          outlineColorMix={outlineColorMix}
          fixedHeight={fixedHeight}
        />
        {/* Invisible hitarea to capture pointer events regardless of opacity - must be after TextLabel to be on top */}
        <div
          className="text-hitarea"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: width * scale,
            height: height * scale,
            background: "transparent",
            cursor: isEditingAnything ? "text" : "pointer",
            zIndex: isEditingAnything ? -1 : 10,
          }}
        />
        {(isHovered || isEditingThisShape) && !fixedHeight && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: width * scale,
              height: height * scale,
              pointerEvents: "none",
              zIndex: 999,
              opacity: 1,
              isolation: "isolate",
            }}
          >
            <svg
              width={width * scale}
              height={height * scale}
              viewBox={`0 0 ${width * scale} ${height * scale}`}
              style={{
                fill: "none",
                stroke: HOVER_EFFECT.COLOR,
                strokeWidth: "2",
                pointerEvents: "none",
                opacity: 1,
              }}
            >
              <rect
                x="0"
                y="0"
                width={width * scale}
                height={height * scale}
                fill="none"
                stroke={HOVER_EFFECT.COLOR}
                strokeWidth="2"
              />
            </svg>
          </div>
        )}
      </>
    );
  }

  addDefaultColorMix(shape: any) {
    this.shapeUtilHelper.addDefaultColorMix(shape);
  }
  addDefaultOutShadow(shape: any) {
    this.shapeUtilHelper.addDefaultOutShadow(shape);
  }
  addDefaultInShadow(shape: any) {
    this.shapeUtilHelper.addDefaultInShadow(shape);
  }

  indicator(shape: any) {
    const bounds = this.editor.getShapeGeometry(shape).bounds;

    // ALWAYS render indicator, ignore isShowIndicator prop
    return (
      <rect
        width={toDomPrecision(bounds.width)}
        height={toDomPrecision(bounds.height)}
        stroke="#338bff"
        strokeWidth="2"
        fill="none"
        opacity="1"
        style={{
          stroke: "#338bff",
          strokeWidth: 2,
          fill: "none",
          opacity: 1,
        }}
      />
    );
  }

  toSvg(shape: CustomTextUtilType, ctx: SvgExportContext) {
    if (shape.props.text) ctx.addExportDef(getFontDefForExport(shape.props.font));

    const bounds = this.editor.getShapeGeometry(shape).bounds;
    const width = bounds.width / (shape.props.scale ?? 1);
    const height = bounds.height / (shape.props.scale ?? 1);

    return (
      <SvgTextLabel
        fontSize={shape.props.size}
        font={shape.props.font}
        fontWeight={shape.props.fontWeight}
        lineHeight={shape.props.lineHeight ?? 1}
        italic={shape.props.italic}
        align={shape.props.textAlign as TLDefaultHorizontalAlignStyle}
        verticalAlign={"middle" as TLDefaultVerticalAlignStyle}
        text={shape.props.text}
        labelColor={shape.props.color}
        bounds={new Box(0, 0, width, height)}
        padding={0}
        editor={this.editor}
      />
    );
  }
  override onResizeStart(shape: CustomTextUtilType) {
    this._preResizeState.delete(shape.id);
    return undefined;
  }

  override onResize(shape: CustomTextUtilType, info: TLResizeInfo<CustomTextUtilType>) {
    const { scaleX, scaleY, handle } = info;

    // Use TLDraw's canonical resize algorithm with proper anchoring
    // resizeBox maintains fixed anchor from initialBounds based on handle
    const resized = resizeBox(shape, info);

    // Calculate dimensions (absolute values)
    const nextWidth = Math.max(1, Math.abs(info.initialBounds.width * scaleX));
    const nextHeight = Math.max(1, Math.abs(info.initialBounds.height * scaleY));

    // Determine flip states based on scale direction
    const nextFlipH = scaleX < 0 ? !shape.props.flipH : shape.props.flipH ?? false;
    const nextFlipV = scaleY < 0 ? !shape.props.flipV : shape.props.flipV ?? false;

    // Check if proportional FIRST (shift held, scale tool, or aspect ratio locked → scaleX ≈ scaleY).
    // When proportional, ALL handles (sides + corners) should change fontSize.
    const isProportional = Math.abs(Math.abs(scaleX) - Math.abs(scaleY)) < 0.001;

    // Handle side resizes (left/right/top/bottom) — only for non-proportional resizes.
    // When proportional (shift or scale tool), side handles fall through to fontSize logic.
    if (!isProportional) {
      if (handle === "left" || handle === "right") {
        return {
          ...resized,
          props: {
            w: nextWidth / shape.props.scale!,
            autoSize: false,
            flipH: nextFlipH,
          },
        };
      } else if (handle === "top" || handle === "bottom") {
        return {
          ...resized,
          props: {
            h: nextHeight / shape.props.scale!,
            fixedHeight: true,
            autoSize: false,
            flipV: nextFlipV,
          },
        };
      }
    }

    if (isProportional) {
      // Shift+corner resize: change fontSize proportionally in real-time.
      //
      // Key insight: tldraw always passes the INITIAL shape (from before resize started)
      // to onResize, not the current shape from the store. So shape.props.size is always
      // the original fontSize — no feedback loop possible.
      //
      // We set autoSize:false + fixedHeight:true to lock geometry to explicit w/h,
      // preventing text reflow from interfering with resize bounds.
      // The original sizing mode is restored in onResizeEnd.
      if (!this._preResizeState.has(shape.id)) {
        this._preResizeState.set(shape.id, {
          autoSize: shape.props.autoSize,
          fixedHeight: shape.props.fixedHeight,
        });
      }

      const newSize = Math.max(1, Math.round(shape.props.size * Math.abs(scaleX)));

      return {
        ...resized,
        props: {
          w: nextWidth / (shape.props.scale ?? 1),
          h: nextHeight / (shape.props.scale ?? 1),
          size: newSize,
          autoSize: false,
          fixedHeight: true,
          flipH: nextFlipH,
          flipV: nextFlipV,
        },
      };
    }

    // Non-proportional corner resizes (no shift).
    // If we were previously doing proportional resize (shift was just released mid-drag),
    // preserve the fontSize to prevent it from reverting to the initial size.
    // This happens because onResize always receives the INITIAL shape, so without
    // explicitly setting `size`, updateShapes overwrites the store with the original fontSize.
    const cornerProps: Record<string, any> = {
      w: nextWidth / shape.props.scale!,
      h: nextHeight / shape.props.scale!,
      fixedHeight: true,
      autoSize: false,
      flipH: nextFlipH,
      flipV: nextFlipV,
    };

    if (this._preResizeState.has(shape.id)) {
      const scaleFactor = Math.max(Math.abs(scaleX), Math.abs(scaleY));
      cornerProps.size = Math.max(1, Math.round(shape.props.size * scaleFactor));
    }

    return {
      ...resized,
      props: cornerProps,
    };
  }

  override onResizeEnd(initial: CustomTextUtilType, current: CustomTextUtilType) {
    const saved = this._preResizeState.get(initial.id);
    if (saved) {
      this._preResizeState.delete(initial.id);

      // Restore the original sizing mode (autoSize/fixedHeight).
      // The fontSize was already set correctly in onResize during the drag.
      // Resizing state calls this via editor.updateShapes(), so returning a partial is correct.
      return {
        id: current.id,
        type: current.type,
        props: {
          autoSize: saved.autoSize,
          fixedHeight: saved.fixedHeight,
        },
      };
    }
  }

  onEditEnd(shape: CustomTextUtilType) {
    const {
      id,
      type,
      props: { text },
    } = shape;

    const trimmedText = shape.props.text.trimEnd();

    if (trimmedText.length === 0) {
      this.editor.deleteShapes([shape.id]);
    } else {
      if (trimmedText !== shape.props.text) {
        this.editor.updateShapes([
          {
            id,
            type,
            props: {
              text: text.trimEnd(),
            },
          },
        ]);
      }
    }
  }

  override onBeforeUpdate(prev: CustomTextUtilType, next: CustomTextUtilType) {
    // Only auto-recalculate dimensions when autoSize is true
    // When fixedHeight is true, we still calculate width but not height
    if (!next.props.autoSize) return;

    const autoSizeChanged = prev.props.autoSize !== next.props.autoSize;
    const styleDidChange =
      prev.props.size !== next.props.size ||
      prev.props.textAlign !== next.props.textAlign ||
      prev.props.font !== next.props.font ||
      prev.props.fontWeight !== next.props.fontWeight ||
      prev.props.letterSpacing !== next.props.letterSpacing ||
      (prev.props.scale !== 1 && next.props.scale === 1);

    const textDidChange = !isEqual(prev.props.text, next.props.text);

    // Check if user is manually resizing (w changed significantly without text/style changes)
    // If w changed but text and styles didn't, user is likely resizing manually
    const wChangedManually =
      Math.abs(prev.props.w - next.props.w) > 1 && !textDidChange && !styleDidChange && !autoSizeChanged;

    // If user is manually resizing, don't recalculate - let onResize handle it
    if (wChangedManually) {
      return;
    }

    // When autoSize is enabled (changed from false to true), always recalculate from current text
    if (autoSizeChanged) {
      // Force recalculation by creating a temporary props object without w
      // This ensures we calculate from text only, ignoring the previous manual width
      // We don't use getMinDimensions here to bypass the cache
      const tempProps = { ...next.props };
      // Temporarily remove w to force getTextSize to calculate from text only
      delete (tempProps as any).w;
      const boundsB = getTextSize(this.editor, { ...tempProps, autoSize: true });
      const wB = boundsB.width * next.props.scale!;

      // Calculate the width difference to adjust position for center/right alignment
      const wA = prev.props.w * prev.props.scale!;
      const widthDiff = wB - wA;

      // Calculate delta based on textAlign to maintain centering
      let delta: Vec | undefined;
      switch (next.props.textAlign) {
        case "middle": {
          // For center align, move half the width difference to shrink from both sides
          delta = new Vec(widthDiff / 2, 0);
          break;
        }
        case "end": {
          // For right align, move the full width difference
          delta = new Vec(widthDiff, 0);
          break;
        }
        default: {
          // For left align, no horizontal movement needed
          delta = undefined;
          break;
        }
      }

      if (delta) {
        // Account for shape rotation when adjusting position
        delta.rot(next.rotation);
        return {
          ...next,
          x: next.x - delta.x,
          y: next.y - delta.y,
          props: { ...next.props, w: wB },
        };
      }

      return {
        ...next,
        props: { ...next.props, w: wB },
      };
    }

    // When autoSize is true and clicking multiple times, always recalculate from current text
    // Ignore next.props.w completely as it may be incorrect (from manual resize)
    if (!styleDidChange && !textDidChange) {
      // Calculate the current visual width from prev props (what's actually displayed)
      const boundsA = getTextSize(this.editor, prev.props);
      const currentVisualWidth = boundsA.width * prev.props.scale!;

      // Force recalculation by creating a temporary props object without w
      // This ensures we calculate from text only, ignoring the previous manual width
      // We don't use getMinDimensions here to bypass the cache
      const tempProps = { ...next.props };
      // Temporarily remove w to force getTextSize to calculate from text only
      delete (tempProps as any).w;
      const boundsB = getTextSize(this.editor, { ...tempProps, autoSize: true });
      const wB = boundsB.width * next.props.scale!;

      // Compare with current visual width to avoid unnecessary updates
      const widthDiff = Math.abs(wB - currentVisualWidth);

      // If the width is essentially the same (within 1px tolerance), don't update
      // This prevents the text from moving when clicking autosize multiple times
      if (widthDiff < 1) {
        return next; // Return unchanged to prevent any movement
      }

      // Only update if there's a significant difference
      return {
        ...next,
        props: { ...next.props, w: wB },
      };
    }

    // When text or style changes, recalculate and adjust position if needed
    // Always calculate boundsA from actual text, not from prev.props.w
    // Call getTextSize directly to bypass cache when needed
    const boundsA = getTextSize(this.editor, prev.props);
    const boundsB = getTextSize(this.editor, next.props);

    const wA = boundsA.width * prev.props.scale!;
    const hA = boundsA.height * prev.props.scale!;
    const wB = boundsB.width * next.props.scale!;
    const hB = boundsB.height * next.props.scale!;

    let delta: Vec | undefined;

    switch (next.props.textAlign) {
      case "middle": {
        delta = new Vec((wB - wA) / 2, textDidChange ? 0 : (hB - hA) / 2);
        break;
      }
      case "end": {
        delta = new Vec(wB - wA, textDidChange ? 0 : (hB - hA) / 2);
        break;
      }
      default: {
        if (textDidChange) break;
        delta = new Vec(0, (hB - hA) / 2);
        break;
      }
    }

    if (delta) {
      // account for shape rotation when writing text:
      delta.rot(next.rotation);
      const { x, y } = next;
      return {
        ...next,
        x: x - delta.x,
        y: y - delta.y,
        props: { ...next.props, w: wB },
      };
    } else {
      return {
        ...next,
        props: { ...next.props, w: wB },
      };
    }
  }
}
